import { Typography, Box, Paper } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  ApiRequest_Create_PropertyLocation,
  ApiResponse_Create_PropertyLocation,
  useCreatePropertyLocation,
} from '@/api/endpoints/propertyLocation/useCreatePropertyLocation';
import { PropertyLocationType } from '@/api/enums';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import FormAddressAutocomplete from '../form/FormAddressAutocomplete';
import FormSelect from '../form/FormSelect';
import FormTextField from '../form/FormTextField';
import ModalWithForm from '../modals/ModalWithForm';

import { propertyLocationDeclaredUnitsCountOptions, propertyLocationTypeOptions } from './options';

interface Props {
  onClose: () => void;
  onPropertyLocationCreated: (propertyLocation: ApiResponse_Create_PropertyLocation) => void;
}

const PropertyLocationCreateModal: React.FC<Props> = ({ onClose, onPropertyLocationCreated }) => {
  const form = useForm<ApiRequest_Create_PropertyLocation>();
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = form;

  const propertyLocationType = watch('type');
  const addressText = watch('address_text');

  const { setSelectedPropertyLocationId } = usePropertyManager();

  const createPropertyLocationMutation = useCreatePropertyLocation({
    onSuccess: data => {
      onPropertyLocationCreated(data);
      setSelectedPropertyLocationId(data.id);
      onClose();
    },
  });

  const onSubmit = useCallback(
    (data: ApiRequest_Create_PropertyLocation) => createPropertyLocationMutation.mutateAsync(data),
    [createPropertyLocationMutation]
  );

  const emailPreview = useMemo(() => {
    if (!addressText) return null;

    // Extract state from address (this is a simplified version)
    const stateMatch = addressText.match(/,\s*([A-Z]{2})/);
    const state = stateMatch ? stateMatch[1] : '';

    // Extract city from address
    const cityMatch = addressText.match(/,\s*([^,]+),\s*[A-Z]{2}/);
    const city = cityMatch ? cityMatch[1].trim() : '';

    // Generate sample name (remove spaces and special characters)
    // Just use the first part of the address (street number and name)
    const streetPart = addressText.split(',')[0]?.trim();
    const formattedName = streetPart?.replace(/[^a-zA-Z0-9]/g, '') || 'property';

    // Always provide at least one fallback option
    const options = [
      `${formattedName}@mail.terr.ai`,
      state ? `${formattedName}.${state}@mail.terr.ai` : null,
      city ? `${formattedName}.${city}@mail.terr.ai` : null,
    ].filter(Boolean);

    return options[0]?.toLowerCase() || null; // Always return at least the first option
  }, [addressText]);

  return (
    <ModalWithForm
      form={form}
      onSubmit={onSubmit}
      onClose={onClose}
      maxWidth="md"
      title="Add Property Location"
      buttonLabel="Add"
    >
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        This is the location that you are renting. A unique email address for this location will be
        generated from the rental property address you enter. Your tenants will use this email to
        contact you.
      </Typography>

      <FormTextField
        label="Enter a name to help you identify this property (e.g Florida Beach House)"
        placeholder="Enter property display name"
        error={!!errors.name}
        helperText={errors.name?.message?.toString()}
        {...register('name')}
        size="small"
        control={control}
        textFieldProps={{
          sx: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              bgcolor: 'background.paper',
              '&:hover': {
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'grey.300',
                },
              },
              '&.Mui-focused': {
                outline: 'none !important',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
              },
            },
          },
        }}
      />

      <FormAddressAutocomplete
        label="Address for the property you are renting (e.g. 123 Main St, Miami, FL 33130)"
        placeholder="Enter property rental address"
        error={!!errors.address_text}
        helperText={errors.address_text?.message?.toString()}
        required
        {...register('address_text', {
          required: 'Property Rental Address is required',
        })}
        size="small"
        control={control}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            bgcolor: 'background.paper',
            '&:hover': {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.300',
              },
            },
            '&.Mui-focused': {
              outline: 'none !important',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
            },
          },
        }}
      />

      {emailPreview && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Email Preview:
            </Typography>
            <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
              {emailPreview}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              This is an example of the email address that will be generated for this property which
              you will use to communicate with your tenants as well as vendors.
            </Typography>
          </Paper>
        </Box>
      )}

      <FormSelect
        label="Property Type"
        placeholder="Select property type"
        error={!!errors.type}
        helperText={errors.type?.message?.toString()}
        required
        {...register('type', {
          required: 'Property Type is required',
        })}
        size="small"
        control={control}
        options={propertyLocationTypeOptions}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            bgcolor: 'background.paper',
            '&:hover': {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.300',
              },
            },
            '&.Mui-focused': {
              outline: 'none !important',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
            },
          },
        }}
      />

      {propertyLocationType === PropertyLocationType.MULTI_UNIT && (
        <FormSelect
          label="Units Count"
          placeholder="Select units count"
          error={!!errors.declared_units_count}
          helperText={errors.declared_units_count?.message?.toString()}
          required
          {...register('declared_units_count', {
            required: 'Units Count is required',
          })}
          size="small"
          control={control}
          options={propertyLocationDeclaredUnitsCountOptions}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.5rem',
              bgcolor: 'background.paper',
              '&:hover': {
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'grey.300',
                },
              },
              '&.Mui-focused': {
                outline: 'none !important',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
              },
            },
          }}
        />
      )}
    </ModalWithForm>
  );
};

export default PropertyLocationCreateModal;
